import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { UpdateEntityStore } from "../../common/store/types";
import { publisherRepresentativeService } from "../services/publisherRepresentativeService";
import {
  PublisherRepresentativeValuesInit,
  getPublisherRepresentativeValues,
  PublisherRepresentativeValuesType,
  PublisherRepresentativeErrorsType,
  publisherRepresentativeDisplayId,
} from "./PublisherRepresentativeCommonStore";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { PublisherRepresentative } from "../../../api/graphql";
import { normalizeAndValidateEmail } from "../../../common/utils";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class PublisherRepresentativeUpdateStore implements UpdateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  confirmDialogOpened: boolean = false;
  confirmDialogTitle: string = "";
  confirmDialogDescription: string = "";
  confirmDialogCallback: (() => void) | null = null;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: PublisherRepresentativeErrorsType = {};
  service = publisherRepresentativeService;
  publisherRepresentativeInstance: PublisherRepresentative | null = null;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda (envío y relectura): la capa del formulario (SavingOverlay)
  // lo bloquea, así nada de lo que se escribiera lo pisa la relectura.
  isSaving: boolean = false;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = PublisherRepresentativeValuesInit();
  nameError: string | undefined;
  roleError: string | undefined;
  emailError: string | undefined;
  phoneError: string | undefined;
  isSignerError: string | undefined;

  private customValidators: CustomValidator<PublisherRepresentativeValuesType>[] =
    [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction?: () => void;
  originalState: any = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Drawer mode flag - when true, close() pops/closes the drawer instead of navigating
  isDrawerMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: { resultId?: string; cancelled?: boolean }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();

    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(
          v as CustomValidator<PublisherRepresentativeValuesType>
        );
      }
    });
    makeAutoObservable(this, {
      originalState: false,
    });
    this.setupChangeDetection();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(
    validator: CustomValidator<PublisherRepresentativeValuesType>
  ) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  *loadPublisherRepresentativeById(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    this.publisherRepresentativeInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (this.publisherRepresentativeInstance) {
      this.entityValues = getPublisherRepresentativeValues(
        this.publisherRepresentativeInstance,
        this.langStore
      );
      this.saveOriginalState();
    }
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  // Guardar deshabilitado: mientras se guarda.
  get saveDisabled() {
    return this.isProcessing;
  }

  get displayId(): string {
    if (this._ownPendingChanges && this.entityValues) {
      return publisherRepresentativeDisplayId(
        this.entityValues as any,
        this.langStore
      );
    }
    if (this.publisherRepresentativeInstance) {
      return publisherRepresentativeDisplayId(
        this.publisherRepresentativeInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PublisherRepresentativeSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "null",
        label: this.rootStore.langStore.groupPublisherRepresentativeLabel,
        errors: this.getCardErrors("null"),
      },
    ];
  }

  returnedData(_data: any): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  showConfirmDialog(title: string, description: string, callback: () => void) {
    this.confirmDialogTitle = title;
    this.confirmDialogDescription = description;
    this.confirmDialogCallback = callback;
    this.confirmDialogOpened = true;
  }

  closeConfirmDialog() {
    this.confirmDialogOpened = false;
    this.confirmDialogCallback = null;
  }

  *executeConfirmedAction() {
    const callback = this.confirmDialogCallback;
    this.closeConfirmDialog();
    if (callback) {
      yield callback();
    }
  }

  getNameValue(): string {
    return this.entityValues.name;
  }

  setNameValue(value: string) {
    this.entityValues.name = value;
  }

  getRoleValue(): string {
    return this.entityValues.role;
  }

  setRoleValue(value: string) {
    this.entityValues.role = value;
  }

  getEmailValue(): string {
    return this.entityValues.email;
  }

  setEmailValue(value: string) {
    this.entityValues.email = value;
  }

  getPhoneValue(): string {
    return this.entityValues.phone;
  }

  setPhoneValue(value: string) {
    this.entityValues.phone = value;
  }

  getIsSignerValue(): boolean {
    return this.entityValues.isSigner;
  }

  setIsSignerValue(value: boolean) {
    this.entityValues.isSigner = value;
  }

  getRowFromInstance(instance: PublisherRepresentative) {
    const row: any = {};
    row.version_ = instance.version_;
    row.id = instance.id;
    row.name = instance.name;
    row.role = instance.role;
    row.email = instance.email;
    row.phone = instance.phone;
    row.isSigner = instance.isSigner;
    return row;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.publisherRepresentativeInstance = null;
    this.entityValues = PublisherRepresentativeValuesInit();
    this.cardState = { null: { open: true, errors: [] } };
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(
    values: PublisherRepresentativeValuesType
  ): PublisherRepresentativeErrorsType {
    const errors: PublisherRepresentativeErrorsType = {};
    this.clearErrors();
    values.name = values.name.trim();
    if (values.name === "") {
      errors.name = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherRepresentativeNameLabel,
      };
    }

    if (values.email) {
      const normalizedEmail = normalizeAndValidateEmail(values.email);
      if (normalizedEmail === null) {
        errors.email = {
          message: this.langStore.badSyntaxEmail,
          fieldLabel: this.langStore.PublisherRepresentativeEmailLabel,
        };
      }
    } else {
      errors.email = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PublisherRepresentativeEmailLabel,
      };
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      this.originalSnapshot ?? undefined,
      "PublisherRepresentativeUpdateStore"
    );
  }

  close(returnData?: { resultId?: string; cancelled?: boolean }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.isDrawerMode) {
      // In drawer mode, close or pop the drawer instead of navigating
      if (this.rootStore.drawerViewStack.length > 1) {
        this.rootStore.popDrawerView();
      } else {
        this.rootStore.closeDrawerView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else {
      this.rootStore.router.goBack();
    }
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      name: this.entityValues.name,
      role: this.entityValues.role,
      email: this.entityValues.email,
      phone: this.entityValues.phone,
      isSigner: this.entityValues.isSigner,
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  resetPendingChanges() {
    this._ownPendingChanges = false;
  }

  getChangedFields(): string[] {
    if (!this.pendingChanges) return [];

    const changed: string[] = [];

    if (this._ownPendingChanges && this.originalSnapshot) {
      const current = this.computeSnapshot();

      Object.keys(current).forEach((key) => {
        if (!_.isEqual(current[key], this.originalSnapshot![key])) {
          changed.push(key);
        }
      });
    }

    return changed;
  }

  *savePublisherRepresentative(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: el primero
    // cambia la versión del registro, y el segundo fallaría con RECORD_MODIFIED
    // aunque los datos ya estén guardados.
    if (this.saveDisabled) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que haga correr la relectura y el
      // cierre como si éste hubiera salido bien.
      this.savedId = null;
      // Desde acá hasta que termina (también la relectura), el formulario está
      // bloqueado por su capa. Pase lo que pase (también si el manejo del error
      // tira), el finally de afuera lo libera.
      this.isSaving = true;
      try {
        try {
          this.status = "processing";
          yield this._save();
        } catch (error) {
          this.status = "error";

          if (error instanceof Error) {
            const apiError = getApiError(error, this.langStore);
            this.showErrorDialog(
              apiError.code,
              apiError.title,
              apiError.message
            );
          } else {
            this.showErrorDialog(this.langStore.serverError, undefined);
          }
        }

        if (!this.savedId) {
          // Sin guardado exitoso (falló, o no había nada que guardar): Guardar se
          // vuelve a habilitar.
          if (this.isProcessing) this.status = "ok";
        } else {
          try {
            this.resetPendingChanges();
            this.saveOriginalState();
          } finally {
            // Hasta acá sigue guardando (isProcessing): Guardar deshabilitado, y un
            // segundo guardado no corre mientras se relee.
            this.status = "ok";
          }
          this.close({ resultId: this.savedId, cancelled: false });
        }
      } finally {
        // Pase lo que pase (también si el manejo del error tira), el formulario
        // se libera: la capa se cierra.
        this.isSaving = false;
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  *cancelPublisherRepresentative(): any {
    if (this.pendingChanges) {
      this.showConfirmDialog(
        this.langStore.confirmCancelTitle,
        this.langStore.exitWithoutSaving,
        this.executeCancelAction.bind(this)
      );
      return;
    }
    yield this.executeCancelAction();
  }

  executeCancelAction(): void {
    this.cancelled = true;
    this._ownPendingChanges = false;

    this.close({ cancelled: true });
  }

  getState(): InitializeParams {
    return {
      id: this.entityValues.id,
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getPublisherRepresentativeValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = PublisherRepresentativeValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
    this.saveOriginalState();
  }

  *_save(): any {
    // Subentidad sin edición standalone: se persiste por el update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const idToLoad = params?.id || params?.duplicateFromId;

      if (idToLoad) {
        yield this.loadPublisherRepresentativeById(idToLoad);

        if (params?.duplicateFromId) {
          this.entityValues.id = "";
        }
      }

      if (params?.callbackData?.filter) {
        Object.assign(this.entityValues, params.callbackData.filter);
      }

      if (params?.uiState?.cardState) {
        Object.assign(this.cardState, params.uiState.cardState);
      }

      if (params?.preloadedData) {
        Object.assign(this.entityValues, params.preloadedData);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
